import { useMemo } from 'react'
import zxcvbn from 'zxcvbn'

export interface PasswordStrength {
  score: number // 0-4
  label: 'Weak' | 'Fair' | 'Good' | 'Strong'
  color: string
  feedback: {
    warning: string
    suggestions: string[]
  }
  isValid: boolean // Meets minimum strength requirement (score >= 3 = Good)
  crackTime: string
}

export function usePasswordStrength(password: string): PasswordStrength {
  return useMemo(() => {
    if (!password) {
      return {
        score: 0,
        label: 'Weak',
        color: 'bg-red-500',
        feedback: { warning: '', suggestions: ['Enter a password'] },
        isValid: false,
        crackTime: '',
      }
    }

    const result = zxcvbn(password)

    const labels: PasswordStrength['label'][] = [
      'Weak',    // score 0
      'Weak',    // score 1
      'Fair',    // score 2
      'Good',    // score 3
      'Strong',  // score 4
    ]

    const colors = [
      'bg-red-500',     // score 0 — Weak
      'bg-red-500',     // score 1 — Weak
      'bg-orange-500',  // score 2 — Fair
      'bg-yellow-500',  // score 3 — Good
      'bg-dextoro',     // score 4 — Strong
    ]

    return {
      score: result.score,
      label: labels[result.score],
      color: colors[result.score],
      feedback: {
        warning: result.feedback.warning || '',
        suggestions: result.feedback.suggestions,
      },
      isValid: result.score >= 3, // Minimum "Good" strength
      crackTime: result.crack_times_display.offline_slow_hashing_1e4_per_second as string,
    }
  }, [password])
}

// Standalone validation function for form validation
export function validatePasswordStrength(password: string): {
  isValid: boolean
  message: string
} {
  if (!password) {
    return { isValid: false, message: 'Password is required' }
  }

  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters' }
  }

  const result = zxcvbn(password)
  
  if (result.score < 3) {
    const suggestion = result.feedback.suggestions[0] || 'Try a stronger password'
    return { isValid: false, message: suggestion }
  }

  return { isValid: true, message: '' }
}
